/**
 * slideTransformApi.ts — client for the DeckorateAPI freehand slide-redesign
 * endpoint.
 *
 *   POST {base}/slides/transform-ai  (application/json) → text/event-stream
 *
 * DeckorateAPI is a SEPARATE FastAPI service from the main ai-api. The base URL
 * comes from `config.deckorateApiBaseUrl` via `useDeckorateHttp()` and ALREADY
 * INCLUDES the service's `/v1/deckorate` prefix — e.g.
 * `https://deckorate-api.<env>/v1/deckorate`. So paths here start at the segment
 * after it (`/slides/...`), matching the `/alignment/fix` and
 * `/content/speaker-notes` clients. Prefixing `/v1/deckorate` again would
 * request `/v1/deckorate/v1/deckorate/...` and 404. Nothing here hardcodes a host.
 *
 * Wire shapes mirror `DeckorateAPI/models/slide_transform/slide_transform.py`
 * one-for-one — including its snake_case field names, the `_xml` alias for the
 * whole-slide document, and the camelCase `shapeTreeXml` on the result. Keep
 * this file the single source of truth for the contract so the rest of the
 * feature stays decoupled from it.
 *
 * Auth is not handled here: callers pass the shared `AuthorizedFetch` from
 * `useDeckorateHttp()` (bearer + APIM key + 401 refresh), so this client only
 * adds the request-shape headers.
 */

import type { AuthorizedFetch } from "../../../services/deckorateApi";
import { consumeSseStream, SseStreamError } from "../../../services/sse";

/** Relative to `config.deckorateApiBaseUrl`, which carries the `/v1/deckorate` prefix. */
const TRANSFORM_PATH = "/slides/transform-ai";

/* ── Request shapes ─────────────────────────────────────────────── */

/**
 * Presentation theme palette as bare 6-digit hex (no leading `#`).
 * All 12 slots are MANDATORY server-side — see `ThemeColors` in the Python
 * model. Build one with `buildThemePalette` rather than by hand.
 */
export interface SlideThemeColors {
  dk1: string;
  lt1: string;
  dk2: string;
  lt2: string;
  accent1: string;
  accent2: string;
  accent3: string;
  accent4: string;
  accent5: string;
  accent6: string;
  hlink: string;
  folHlink: string;
}

/** Slide size in EMU (English Metric Units). */
export interface SlideDimensions {
  cx: number;
  cy: number;
}

export interface SlideTransformInput {
  /** 0-based index of the slide in the deck. */
  slide_index: number;
  /** The entire `ppt/slides/slideN.xml` document, verbatim. */
  _xml: string;
  slide_dimensions?: SlideDimensions;
  /**
   * Optional rendered-image context. Omitted for an XML-only redesign, which is
   * what the add-in sends — the add-in has no slide-render pipeline, and the
   * backend treats both fields as optional.
   */
  image_blob_container?: string;
  image_blob_name?: string;
}

export interface SlideTransformTheme {
  theme_colors: SlideThemeColors;
  /** Typeface applied to all redesigned text. Server defaults when omitted. */
  font_family?: string;
}

export interface SlideTransformRequest {
  request_id: string;
  slides: SlideTransformInput[];
  theme: SlideTransformTheme;
}

/* ── SSE payload shapes ─────────────────────────────────────────── */

/** `event: status` — per-slide progress notification. */
export interface SlideTransformStatusEvent {
  request_id: string;
  slide_index: number;
  stage: string;
  state: string;
  icon: string;
}

/** `event: token` — a raw model-output chunk, streamed for live feedback. */
export interface SlideTransformTokenEvent {
  request_id: string;
  slide_index: number;
  text: string;
}

/** `event: result` — the final redesigned shape tree for one slide. */
export interface SlideTransformResultEvent {
  request_id: string;
  slide_index: number;
  /**
   * The redesigned `<p:spTree>` INNER xml — shape elements only, with no
   * `<p:sld>`/`<p:cSld>`/`<p:spTree>` wrapper and no `nvGrpSpPr`/`grpSpPr`
   * header (the backend strips those). Splice it into the slide's existing
   * shape tree after that header — see `spliceShapeTree`.
   *
   * Never unparseable: when the redesign fails validation even after the retry
   * round the backend substitutes the slide's own pre-redesign shapes, so this
   * is always safe to splice. `valid`/`error` still report that the redesign
   * itself failed, so callers should check `valid` before claiming success.
   */
  shapeTreeXml: string;
  valid: boolean;
  retried?: boolean;
  error?: string | null;
}

/** `event: error` — a terminal error for one slide (HTTP is already 200). */
export interface SlideTransformErrorEvent {
  error: {
    code: string;
    message: string;
    details?: unknown;
    request_id?: string;
  };
  slide_index: number;
}

/* ── Errors ─────────────────────────────────────────────────────── */

/** Raised for HTTP-level failures, i.e. before the 200 stream opens. */
export class SlideTransformApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly httpStatus?: number,
  ) {
    super(message);
    this.name = "SlideTransformApiError";
  }
}

/* ── Event callbacks ────────────────────────────────────────────── */

export interface SlideTransformStreamEvents {
  onStatus?: (data: SlideTransformStatusEvent) => void;
  onToken?: (data: SlideTransformTokenEvent) => void;
  onResult?: (data: SlideTransformResultEvent) => void;
  onError?: (data: SlideTransformErrorEvent) => void;
}

export interface SlideTransformStreamOptions {
  /** DeckorateAPI base URL (`config.deckorateApiBaseUrl`). */
  readonly baseUrl: string;
  /** Shared authorised fetch from `useDeckorateHttp()`. */
  readonly authorizedFetch: AuthorizedFetch;
  readonly signal?: AbortSignal;
  /** Reports a skipped malformed SSE frame. Injected so this module stays log-agnostic. */
  readonly onMalformedFrame?: (event: string, error: unknown) => void;
}

const EVENT_STATUS = "status";
const EVENT_TOKEN = "token";
const EVENT_RESULT = "result";
const EVENT_ERROR = "error";

/**
 * Guard the parsed payload before handing it to a callback. The backend is
 * trusted, but a contract drift (renamed field, null payload) would otherwise
 * surface as a confusing downstream TypeError instead of a skipped frame.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function hasSlideIndex(value: unknown): value is { slide_index: number } {
  return isRecord(value) && typeof value.slide_index === "number";
}

function isResultEvent(value: unknown): value is SlideTransformResultEvent {
  return hasSlideIndex(value) && typeof (value as SlideTransformResultEvent).shapeTreeXml === "string";
}

function isErrorEvent(value: unknown): value is SlideTransformErrorEvent {
  return isRecord(value) && isRecord(value.error) && typeof value.error.message === "string";
}

/** Dispatch one parsed SSE frame to the matching callback. */
function dispatch(
  event: string,
  data: unknown,
  events: SlideTransformStreamEvents,
  onMalformedFrame: SlideTransformStreamOptions["onMalformedFrame"],
): void {
  switch (event) {
    case EVENT_STATUS:
      if (hasSlideIndex(data)) events.onStatus?.(data as SlideTransformStatusEvent);
      else onMalformedFrame?.(event, new Error("status frame is missing slide_index"));
      break;
    case EVENT_TOKEN:
      if (hasSlideIndex(data)) events.onToken?.(data as SlideTransformTokenEvent);
      else onMalformedFrame?.(event, new Error("token frame is missing slide_index"));
      break;
    case EVENT_RESULT:
      if (isResultEvent(data)) events.onResult?.(data);
      else onMalformedFrame?.(event, new Error("result frame is missing shapeTreeXml"));
      break;
    case EVENT_ERROR:
      if (isErrorEvent(data)) events.onError?.(data);
      else onMalformedFrame?.(event, new Error("error frame is missing error.message"));
      break;
    default:
      // Unknown event names are ignored so a server-side addition is backwards compatible.
      break;
  }
}

/** Throws SlideTransformApiError for a non-200 response, parsing the error envelope. */
async function throwForHttpError(res: Response): Promise<never> {
  let code = `HTTP_${res.status}`;
  let message = `Slide transformation failed (${res.status})`;
  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string } };
    if (body?.error?.code) code = body.error.code;
    if (body?.error?.message) message = body.error.message;
  } catch {
    /* non-JSON error body — keep the status-derived defaults */
  }
  throw new SlideTransformApiError(code, message, res.status);
}

function buildTransformUrl(baseUrl: string): string {
  return `${baseUrl.replace(/\/$/, "")}${TRANSFORM_PATH}`;
}

/**
 * POST the redesign request and consume the SSE response, invoking `events`
 * as frames arrive. Resolves when the stream ends.
 *
 * Throws {@link SlideTransformApiError} for HTTP-level failures and for a 200
 * with no body stream. Per-slide failures arrive as `error` frames instead, and
 * do not reject — the stream continues with the remaining slides.
 */
export async function streamSlideTransform(
  request: SlideTransformRequest,
  events: SlideTransformStreamEvents,
  options: SlideTransformStreamOptions,
): Promise<void> {
  const res = await options.authorizedFetch(buildTransformUrl(options.baseUrl), {
    method: "POST",
    headers: { Accept: "text/event-stream", "Content-Type": "application/json" },
    body: JSON.stringify(request),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  if (!res.ok) await throwForHttpError(res);

  try {
    await consumeSseStream(
      res.body,
      (event, data) => dispatch(event, data, events, options.onMalformedFrame),
      { onMalformedFrame: options.onMalformedFrame },
    );
  } catch (e) {
    if (e instanceof SseStreamError) throw new SlideTransformApiError(e.code, e.message);
    throw e;
  }
}
